import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

import type { DocumentListing } from './useDocuments';

/**
 * 폴더 공유·이동·감사 데이터 접근.
 *
 * <h2>공개 범위는 두 층이다</h2>
 * 하나는 <b>구역</b>({@link AccessView.areaLabel}) — `mypage/`·`docs/공용/`·`docs/dept/{부서}/` 처럼
 * 자리 자체가 뜻을 갖는 곳이고, 코드가 정하므로 화면에서 못 고친다. 다른 하나는 <b>공유</b>
 * ({@link AccessView.direct}) — 사람이 폴더에 매긴 것이다. 화면은 이 둘을 <b>섞어 한 줄로 보여 주면
 * 안 된다</b>: 왜 못 고치는지 물었을 때 답할 자리가 없어진다.
 *
 * <h2>상속은 여기서 못 고친다</h2>
 * {@link AccessView.inherited} 는 상위 폴더에서 온 줄이다. 고치려면 그 폴더로 가야 하고, 화면은
 * 그 사실을 말해야 한다. 조상과는 AND 로 걸리므로 <b>여기서 넓힐 방법은 없다</b>.
 */

export type ShareScope = 'ALL' | 'DEPARTMENT' | 'USER';

/**
 * 공유 한 줄.
 *
 * @property prefix 이 줄이 걸려 있는 자리. 상속된 줄이면 지금 보는 키보다 <b>짧다</b>
 * @property label  사람이 읽는 말(`전사`·`부서 · 인사총무팀`·`인원 · 홍길동`). 서버가 붙여 준다 —
 *                  화면이 id 로 이름을 다시 조회하면 폴더 하나 그리는 데 왕복이 는다
 */
export type ShareRow = {
  prefix: string;
  scopeType: ShareScope;
  /** ⚠ 백엔드가 null 필드를 JSON 에서 빼므로 `undefined` 로 온다 — `== null` 로 비교한다. */
  scopeRefId?: string | null;
  label: string;
};

export type AccessView = {
  storageKey: string;
  /** 구역 이름. 코드가 정하며 화면에서 못 고친다. */
  areaLabel: string;
  areaNote: string;
  direct: ShareRow[];
  inherited: ShareRow[];
  editable: boolean;
  /** 위 전부를 합친 한 줄. 뱃지에 그대로 쓴다. */
  summary: string;
};

export type ShareInput = { scopeType: ShareScope; scopeRefId?: string | null };

export function s3AccessQuery(key: string) {
  return {
    queryKey: ['docs-s3-access', key] as const,
    queryFn: () => apiFetch<AccessView>(`/v1/documents/browse/s3/access?key=${encodeURIComponent(key)}`),
  };
}

export function useS3Access(key: string | null) {
  return useQuery({ ...s3AccessQuery(key ?? ''), enabled: !!key, staleTime: 30_000 });
}

/**
 * 목록의 폴더마다 뱃지를 그릴 때. <b>키가 없으면 아예 안 부른다</b> — 빈 배열을 POST 하면
 * 서버 왕복만 늘고 답은 늘 빈 배열이다.
 */
export function useS3AccessBatch(keys: string[], enabled = true) {
  const joined = keys.join(' ');
  return useQuery({
    queryKey: ['docs-s3-access-batch', joined] as const,
    queryFn: () => apiFetch<AccessView[]>('/v1/documents/browse/s3/access/batch', { method: 'POST', body: keys }),
    enabled: enabled && keys.length > 0,
    staleTime: 30_000,
  });
}

/** 공유·폴더가 바뀌면 목록·나무·뱃지가 함께 낡는다. 한 곳에서 같이 무효화한다. */
function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['docs-browse-s3'] });
  queryClient.invalidateQueries({ queryKey: ['docs-s3-access'] });
  queryClient.invalidateQueries({ queryKey: ['docs-s3-access-batch'] });
  queryClient.invalidateQueries({ queryKey: ['docs-folder-stats-s3'] });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { parentPrefix: string; name: string; shares?: ShareInput[] }) =>
      apiFetch<AccessView>('/v1/documents/browse/s3/folders', { method: 'POST', body }),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useUpdateShares() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { prefix: string; shares: ShareInput[] }) =>
      apiFetch<AccessView>('/v1/documents/browse/s3/shares', { method: 'PUT', body }),
    onSuccess: () => invalidateAll(queryClient),
  });
}

// ── 옮기기 ────────────────────────────────────────────────────────────────────

export type MovePlanItem = {
  sourceKey: string;
  targetKey: string;
  before: AccessView;
  after: AccessView;
  /** 공개 범위가 바뀌는가. 화면이 경고를 띄울 근거다. */
  changed: boolean;
  /** 바뀌는 방향이 <b>넓어지는</b> 쪽인가 — 좁아지는 것보다 위험하다. */
  widened: boolean;
  note: string;
};

export type MovePlan = {
  targetPrefix: string;
  items: MovePlanItem[];
  /** 하나라도 권한이 바뀌면 true. 실제 이동은 `confirmed: true` 를 요구한다. */
  requiresConfirm: boolean;
};

export type MoveResult = {
  objects: number;
  /**
   * 함께 갈아 끼운 DB 칸 수. <b>0 이면 화면이 감추면 안 된다</b> — 어느 표도 그 파일을 가리키지
   * 않았거나, 서버가 모르는 칸이 가리키고 있다는 뜻이고 뒤쪽이면 그 링크는 죽는다.
   */
  dbUpdates: number;
  failures: string[];
};

export function useMovePreview() {
  return useMutation({
    mutationFn: (body: { keys: string[]; targetPrefix: string }) =>
      apiFetch<MovePlan>('/v1/documents/browse/s3/move/preview', {
        method: 'POST',
        body: { ...body, confirmed: false },
      }),
  });
}

export function useMove() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { keys: string[]; targetPrefix: string; confirmed: boolean }) =>
      apiFetch<MoveResult>('/v1/documents/browse/s3/move', { method: 'POST', body }),
    onSuccess: () => invalidateAll(queryClient),
  });
}

// ── 이름 검색 ─────────────────────────────────────────────────────────────────

/**
 * 저장소를 <b>이름으로</b> 훑는다. AI 검색(`POST /documents/search`)과 다른 통로다 — 그쪽은 자료실에
 * 등록된 문서를 본문까지 뒤지고, 이쪽은 저장소에 <b>지금 있는 것</b>을 이름만 보고 찾는다.
 *
 * <p>결과가 잘리면 `available` 은 그대로 true 이고 `message` 에 사유가 실린다. 화면은 그것을
 * <b>실패로 그리면 안 된다</b> — 찾은 결과를 못 믿고 버리게 된다.
 */
export function useS3Search(query: string, prefix: string, enabled: boolean) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['docs-s3-search', trimmed, prefix] as const,
    queryFn: () =>
      apiFetch<DocumentListing>(
        `/v1/documents/browse/s3/search?q=${encodeURIComponent(trimmed)}`
          + (prefix ? `&prefix=${encodeURIComponent(prefix)}` : ''),
      ),
    enabled: enabled && trimmed.length > 0,
    staleTime: 15_000,
  });
}

// ── 감사 기록 ─────────────────────────────────────────────────────────────────

export type AuditAction = 'VIEW' | 'DOWNLOAD' | 'FOLDER_CREATE' | 'SHARE_UPDATE' | 'MOVE';

export type StorageAudit = {
  id: string;
  occurredAt: string;
  actorId?: string | null;
  /** 계정이 지워진 뒤에도 읽히도록 서버가 <b>박아 둔</b> 이름. */
  actorName?: string | null;
  action: AuditAction;
  storageKey?: string | null;
  targetKey?: string | null;
  /** 그때의 공개 범위. 재계산으로는 못 얻는 값이라 그 시점의 판단이 그대로 남는다. */
  accessBefore?: string | null;
  accessAfter?: string | null;
  result: 'ALLOW' | 'DENY';
  detail?: string | null;
  bytes?: number | null;
  ip?: string | null;
  userAgent?: string | null;
};

export type AuditPage = { rows: StorageAudit[]; total: number; page: number; size: number };

export type AuditFilter = {
  actorId?: string | null;
  action?: AuditAction | null;
  prefix?: string | null;
  from?: string | null;
  to?: string | null;
  page?: number;
  size?: number;
};

export function useStorageAudit(filter: AuditFilter, enabled = true) {
  const params = new URLSearchParams();
  if (filter.actorId) params.set('actorId', filter.actorId);
  if (filter.action) params.set('action', filter.action);
  if (filter.prefix) params.set('prefix', filter.prefix);
  if (filter.from) params.set('from', filter.from);
  if (filter.to) params.set('to', filter.to);
  params.set('page', String(filter.page ?? 0));
  params.set('size', String(filter.size ?? 50));
  const qs = params.toString();

  return useQuery({
    queryKey: ['docs-storage-audit', qs] as const,
    queryFn: () => apiFetch<AuditPage>(`/v1/documents/audit?${qs}`),
    enabled,
  });
}

// ── AI 내용 검색 ──────────────────────────────────────────────────────────────

/**
 * 문서 <b>내용</b> 검색 결과 한 조각(청크).
 *
 * @property score      코사인 거리 — <b>작을수록 가깝다</b>. 화면에서 큰 값을 좋게 그리면 정확히 거꾸로다
 * @property sourceKind `OCR` 이면 스캔 문서를 기계가 읽은 것이라 오탈자가 섞인다. 화면이 그 사실을 배지로 말한다
 * @property clauseRef  근거 조항("제23조"). 없는 문서는 빠진다
 */
export type DocSearchHit = {
  documentFileId: string;
  documentName: string;
  categoryId?: string | null;
  chunkIndex: number;
  content: string;
  clauseRef?: string | null;
  sourceKind?: string | null;
  updatedAt?: string | null;
  score: number;
  /**
   * 이 문서의 S3 사본 키. 서버가 <b>구역·공유 판정에 쓰려고</b> 이미 읽어 온 값이라 함께 온다 —
   * 결과 줄을 누를 때 자리를 다시 물어볼 필요가 없다.
   *
   * <p>NAS 에만 있는 문서는 없다(undefined). 그때는 자료실 나무로 옮겨 갈 자리가 애초에 없다.
   */
  storageKey?: string | null;
};

/**
 * @property available false 면 <b>검색을 못 했다</b>(에이전트가 꺼져 있거나 임베딩 실패).
 *                     0건과 <b>다르다</b> — 같게 그리면 사람은 "그런 문서가 없다" 고 결론짓는다
 */
export type DocSearchResponse = { available: boolean; message?: string | null; hits: DocSearchHit[] };

/**
 * 사람이 친 말로 문서 <b>내용</b>을 찾는다.
 *
 * <p>이름 검색({@link useS3Search})과 통로가 다르다. 그쪽은 저장소를 훑어 <b>파일 이름</b>을 보고,
 * 이쪽은 색인된 문서의 <b>본문</b>을 벡터로 견준다. 그래서 이름을 모를 때 쓴다 —
 * "출장비 정산 며칠 안에" 같은 질문이 그렇다.
 *
 * <p>인가는 백엔드가 SQL 술어로 건다. 권한 밖 문서의 청크는 <b>애초에 읽히지 않는다</b>.
 */
export function useDocContentSearch() {
  return useMutation({
    mutationFn: (body: { query: string; topK?: number }) =>
      apiFetch<DocSearchResponse>('/v1/documents/search/text', { method: 'POST', body }),
  });
}

/**
 * 문서의 <b>저장소 자리</b>를 한 건 물어본다.
 *
 * <p>대개는 필요 없다 — 내용 검색 결과는 {@link DocSearchHit.storageKey} 를 이미 싣고 온다.
 * 이 함수는 그 값이 없는 자리(다른 화면에서 문서 id 만 들고 온 경우)를 위한 <b>보조</b>다.
 */
export async function fetchDocumentS3Key(documentFileId: string): Promise<string | null> {
  const file = await apiFetch<{
    targets?: { targetType: string; storageKey?: string | null; uploadStatus?: string | null }[];
  }>(`/v1/documents/files/${documentFileId}`);

  // S3 사본만 본다. NAS 사본의 상대경로는 이 화면의 프리픽스와 뜻이 다르다.
  // 지워진 사본(`DELETED`)은 자리를 가리키지 않으므로 건너뛴다 — 그 키로 옮겨 가면 빈 폴더가 뜬다.
  const target = (file.targets ?? []).find(
    (row) => row.targetType === 'S3' && !!row.storageKey && row.uploadStatus !== 'DELETED',
  );
  return target?.storageKey ?? null;
}

// ── 폴더 삭제 요청 ────────────────────────────────────────────────────────────

/**
 * 폴더 삭제를 <b>전산 담당에게 요청</b>한다. 지우지 않는다 — 부탁만 한다.
 *
 * <p>돌아오는 값은 요청이 <b>배달된 담당자 수</b>다. 0 이면 서버가 먼저 오류를 낸다(아무에게도
 * 안 간 요청을 접수됐다고 하면 안 된다).
 *
 * <p>완료되면 요청자의 할일에 "요청하신 폴더 삭제가 완료되었습니다" 가 뜬다 — 전산 담당이 그
 * 할일을 완료로 체크하는 순간 서버가 되돌려 보낸다.
 */
export function useRequestFolderDeletion() {
  return useMutation({
    mutationFn: (body: { prefix: string; reason: string }) =>
      apiFetch<number>('/v1/documents/browse/s3/delete-request', { method: 'POST', body }),
  });
}
